import React, { useState } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BroadcastVideoSlot from '../broadcast/BroadcastVideoSlot';
import BroadcastControls from '../broadcast/BroadcastControls';
import { useBroadcastSync } from '../broadcast/hooks/useBroadcastSync';

function BroadcastViewer({ videoDataArray = [], eventLabel = '', onBack }) {
  const {
    slots,
    primaryIndex,
    timeline,
    playerRefs,
    play,
    pause,
    scrub,
    skip,
    promote,
    updateVideoData,
    needsGreenFlag,
    leadIn,
  } = useBroadcastSync(videoDataArray);

  // Which slot (if any) is in solo-play mode for green flag setting.
  // While a slot is solo, the main timeline is paused.
  const [soloIndex, setSoloIndex] = useState(null);

  const handleStartSolo = (i) => {
    pause();
    setSoloIndex(i);
  };

  const handleEndSolo = () => {
    setSoloIndex(null);
  };

  const maxAnchor = videoDataArray.reduce((max, v) => {
    const a = v?.greenFlag ?? v?.crossStart ?? 0;
    return Math.max(max, a);
  }, 0);
  const estimatedDuration = 10000 + 15 + maxAnchor + 60 * 60;

  return (
    <Box sx={{ bgcolor: 'grey.950', minHeight: '100vh', p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Tooltip title="Back to selection">
          <IconButton onClick={onBack} sx={{ color: 'white' }}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="h6" color="white" noWrap>
          {eventLabel || 'Broadcast Viewer'}
        </Typography>
      </Box>

      {/* Main layout */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateAreas: {
            xs: '"primary" "secondary1" "secondary2"',
            md: '"primary secondary1" "primary secondary2"',
          },
          gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
          gridTemplateRows: { xs: 'auto', md: '1fr 1fr' },
          gap: 1,
          mb: 1,
        }}
      >
        {slots.map((videoData, i) => {
          const isPrimary = i === primaryIndex;
          const secondarySlots = [0, 1, 2].filter((n) => n !== primaryIndex);
          const secondaryArea = `secondary${secondarySlots.indexOf(i) + 1}`;
          // Solo mode: only the solo slot plays; main isPlaying is false (paused above)
          const slotIsPlaying = soloIndex !== null ? false : timeline.isPlaying;
          return (
            <Box
              key={videoData?.id || i}
              sx={{ gridArea: isPrimary ? 'primary' : secondaryArea }}
            >
              <BroadcastVideoSlot
                videoData={videoData}
                playerRef={(el) => { playerRefs.current[i] = el; }}
                playerRefs={playerRefs}
                slotIndex={i}
                isPrimary={isPrimary}
                isPlaying={slotIsPlaying}
                playbackRate={timeline.playbackRate}
                onPromote={() => promote(i)}
                needsGreenFlag={needsGreenFlag(videoData)}
                onGreenFlagSave={updateVideoData}
                onStartSolo={() => handleStartSolo(i)}
                onEndSolo={handleEndSolo}
              />
            </Box>
          );
        })}
      </Box>

      {/* Controls — disabled during solo play */}
      <BroadcastControls
        isPlaying={timeline.isPlaying}
        timelineTime={timeline.currentTime}
        duration={estimatedDuration}
        leadIn={leadIn}
        onPlay={soloIndex !== null ? undefined : play}
        onPause={pause}
        onScrub={soloIndex !== null ? undefined : scrub}
        onSkip={soloIndex !== null ? undefined : skip}
      />
    </Box>
  );
}

export default BroadcastViewer;
